import React, { useState, useEffect } from 'react';
import { StyleSheet, Text, Pressable, View, Image, ScrollView } from 'react-native';
import Images from '../constants/Images';
import BaseText from './BaseText';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../i18n';
import { Color, CommonProperties, CommonStyles } from '../styles';
import Button from './Button';
import { useDispatch, useSelector } from 'react-redux';
import { selectHomepageOnewayPopupDetails } from '../store/selectors/profile.selector';
import PickerSelect from './PickerSelect';
import InputForm from './InputForm';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Screens } from '../constants/ScreenNames';
import { addHomepageUDIDdata } from '../store/actions/search';
import { isTextValidHomepageUdid, udidDataTypes } from '../utils/customFieldUtils';
import DateInput from './DateInput';
import useTextMonths from '../utils/useTextMonths';

export default function HomepageOnewayModal() {
  const { t } = useTranslation(i18nNS.searchWidget);
  const { tKey } = useComponentTKey('TripSearchWidget');
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useDispatch();
  const homepageOneWayDetails = useSelector(selectHomepageOnewayPopupDetails);
  const searchTripLoading = useSelector((s) => s.search.tripResultsLoading);
  const { onSubmit } = route?.params || {};
  function onPressFunction() {
    navigation.goBack();
  }
  const [months] = useTextMonths();
  const [udidData, setUdidData] = useState({});

  useEffect(() => {
    // Initialize the state with default values for each item
    const initialState = [];
    homepageOneWayDetails.forEach((detail) => {
      initialState.push({
        UDIDNum: detail?.Number,
        text: '',
        UDIDEntryType: detail?.UDIDEntryType,
        Description: detail?.Description,
      });
    });

    setUdidData(initialState);
  }, [homepageOneWayDetails]);

  function handleOnSubmit() {
    if (udidData?.length > 0) {
      dispatch(addHomepageUDIDdata(udidData));
    }
    onSubmit();
    navigation.navigate(Screens.Home);
  }

  // start web api udid capturing in purchase-
  function getCompanyUDIDOptionId(value) {
    const id = homepageOneWayDetails?.map((detail) => {
      return detail?.ReportingGroupOptions?.find((data) => data.UdidOptionText === value);
    })[0]?.CompanyUDIDOptionsKey;
    return id;
  }
  const [inputErrors, setInputErrors] = useState(Array(homepageOneWayDetails.length).fill(''));

  function isError(text, dataType, index) {
    const errorMessage = isTextValidHomepageUdid(text, dataType);
    setInputErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      newErrors[index] = errorMessage;
      return newErrors;
    });
  }
  const isRequired = homepageOneWayDetails.some((detail, index) => {
    return detail?.IsMandatory === true && !udidData[index]?.text;
  });
  const isContinueDisabled =
    inputErrors.some((error) => error !== '') || isRequired || searchTripLoading;

  return (
    <ScrollView style={styles.mainViewStyle}>
      <Image source={Images['tripeasyLogo']} style={styles.logoStyle} />
      <BaseText style={styles.heading}>{t(tKey('HomepageUDIDmodalHeading'))}</BaseText>
      {homepageOneWayDetails?.length > 0 &&
        homepageOneWayDetails?.map((detail, index) => {
          if (detail?.ReportingGroupOptions?.length > 0) {
            const items = detail?.ReportingGroupOptions?.map((item) => {
              return {
                label: item?.UdidOptionText,
                value: item?.UdidOptionText,
              };
            });
            const isMandatory = detail?.IsMandatory === true;
            return (
              <PickerSelect
                isLoyalty={true}
                errorMessage={
                  isMandatory && udidData[index]?.text === '' ? 'This field is required.' : ''
                }
                key={detail.Id}
                onValueChange={(value) => {
                  const updatedData = [...udidData];
                  updatedData[index] = {
                    ...updatedData[index], // Preserve existing properties
                    text: value?.value,
                    CompanyUDIDOptionID: getCompanyUDIDOptionId(value.value),
                  };
                  setUdidData(updatedData);
                }}
                items={items}
                label={detail?.Text}
                value={udidData[index]?.text || ''}
                useDropdown={true}
              />
            );
          }
        })}
      {homepageOneWayDetails?.length > 0 &&
        homepageOneWayDetails?.map((detail, index) => {
          if (detail?.TextEntry !== 7 && detail?.ReportingGroupOptions?.length === 0) {
            const dataType = udidDataTypes[detail?.TextEntry];
            const isMandatory = detail?.IsMandatory === true;
            return (
              <InputForm
                errorMessage={
                  isMandatory && udidData[index]?.text === ''
                    ? 'This field is required.'
                    : isTextValidHomepageUdid(udidData[index]?.text, dataType)
                }
                maxLength={detail?.DataLength > 0 ? detail?.DataLength : 30}
                keyboardType={detail?.TextEntry === 3 ? 'numeric' : 'default'}
                key={detail.Id}
                value={udidData[index]?.text || ''}
                placeholder={detail?.Text}
                clearButtonMode="always"
                onChangeText={(text) => {
                  isError(text, dataType, index);
                  const updatedData = [...udidData];
                  updatedData[index] = {
                    ...updatedData[index], // Preserve existing properties
                    text: text,
                  };
                  setUdidData(updatedData);
                }}
                required={isMandatory}
              />
            );
          }
        })}
      {homepageOneWayDetails?.length > 0 &&
        homepageOneWayDetails?.map((detail, index) => {
          if (detail.TextEntry === 7) {
            const dataType = udidDataTypes[detail?.TextEntry];
            return (
              <DateInput
                key={detail?.Id}
                errorMessage={isTextValidHomepageUdid(udidData[index]?.text, dataType)}
                date={udidData[index]?.text || ''}
                isEditModeEnabled={true}
                onDateChange={(value) => {
                  const updatedData = [...udidData];
                  updatedData[index] = {
                    ...updatedData[index],
                    text: value,
                  };
                  setUdidData(updatedData);
                }}
                label={detail.Description}
                months={months}
                labelMonth="Month"
              />
            );
          }
        })}
      <Button
        style={styles.submitButton}
        label={t(tKey('HomepageUDIDmodalContinue'))}
        labelStyle={CommonStyles.h2}
        loading={searchTripLoading}
        disabled={isContinueDisabled}
        onPress={handleOnSubmit}
      />
      <Pressable
        onPress={onPressFunction}
        style={styles.pressableStyle}
        disabled={searchTripLoading}
      >
        <Text style={styles.pressableTextStyle}>{t(tKey('HomepageUDIDmodalBackToSearch'))}</Text>
      </Pressable>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  logoStyle: {
    width: 210,
    height: 60,
    marginTop: 10,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  heading: {
    fontSize: 16,
    marginTop: 16,
    marginBottom: 16,
  },
  submitButton: {
    ...CommonProperties.py3,
    ...CommonProperties.shadow,
    ...CommonProperties.borderRadius4,
  },
  pressableStyle: {
    marginTop: 14,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  pressableTextStyle: {
    fontSize: 18,
    color: Color.primary,
    fontWeight: '500',
  },
  mainViewStyle: {
    marginLeft: 14,
    marginRight: 14,
    marginBottom: 14,
  },
});
